
import { useState, useEffect, useCallback } from 'react';

export interface BaseFilters {
  [key: string]: any;
}

interface UseFiltersOptions<T extends BaseFilters> {
  initialFilters?: T;
  onFilterChange?: (filters: T) => void;
}

export function useFilters<T extends BaseFilters>({ 
  initialFilters, 
  onFilterChange 
}: UseFiltersOptions<T> = {}) {
  // Initialize with provided filters or empty object cast to T
  const [filters, setFilters] = useState<T>(initialFilters || {} as T);
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);
  
  // Update filters state
  const updateFilters = useCallback((newFilters: Partial<T>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      if (onFilterChange) {
        onFilterChange(updated);
      }
      return updated;
    });
  }, [onFilterChange]);
  
  // Reset filters to initial state or empty
  const resetFilters = useCallback(() => {
    const reset = initialFilters || {} as T;
    setFilters(reset);
    if (onFilterChange) {
      onFilterChange(reset);
    }
  }, [initialFilters, onFilterChange]);
  
  // Toggle a specific boolean filter
  const toggleFilter = useCallback((key: keyof T) => {
    setFilters(prev => {
      const updated = { 
        ...prev, 
        [key]: typeof prev[key] === 'boolean' ? !prev[key] : prev[key] 
      };
      if (onFilterChange) {
        onFilterChange(updated);
      }
      return updated;
    });
  }, [onFilterChange]);
  
  // Toggle an item in an array filter
  const toggleArrayFilter = useCallback((key: keyof T, value: any) => {
    setFilters(prev => {
      if (!Array.isArray(prev[key])) {
        console.warn(`Filter ${String(key)} is not an array`);
        return prev;
      }
      
      const currentArray = [...prev[key]];
      const index = currentArray.indexOf(value);
      
      if (index >= 0) {
        currentArray.splice(index, 1);
      } else {
        currentArray.push(value);
      }
      
      const updated = { ...prev, [key]: currentArray };
      if (onFilterChange) {
        onFilterChange(updated);
      }
      return updated;
    });
  }, [onFilterChange]);
  
  // Check if filters are active
  useEffect(() => {
    // Helper to check if a filter has an active value
    const isFilterActive = (key: string, value: any): boolean => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => 
          v !== undefined && 
          v !== null && 
          v !== '' && 
          (Array.isArray(v) ? v.length > 0 : true)
        );
      }
      return value !== undefined && value !== null && value !== '';
    };
    
    // Check all filters
    const active = Object.entries(filters).some(([key, value]) => 
      isFilterActive(key, value)
    );
    
    setHasActiveFilters(active);
  }, [filters]);
  
  return {
    filters,
    hasActiveFilters,
    updateFilters,
    resetFilters,
    toggleFilter,
    toggleArrayFilter,
    setFilters
  };
}
